/**
 * Piano Analytics Constants
 *
 * Naming conventions:
 * - page: lowercase with spaces (e.g., "favorite apps")
 * - page_chapter: PascalCase (e.g., "MyApps")
 * - click: lowercase, verb + noun (e.g., "save favorite apps")
 * - click_chapter: lowercase (e.g., "apps")
 */

/**
 * Module name - maps to page_chapter2 in Piano Analytics
 */
export const MODULE_NAME = 'B2O Container Module' as const;

export const PIANO_EVENTS = {
  // Login events
  LOGIN: {
    EXECUTE: {
      click: 'execute login',
      page: 'login',
      chapter1: 'authentication',
    },
    ERROR: {
      click: 'login error',
      page: 'login',
      chapter1: 'authentication',
    },
  },

  // Homepage events
  HOMEPAGE: {
    VIEW: {
      page: 'homepage',
      chapter1: 'MyApps',
      chapter2: 'Dashboard',
    },
    TAB_RETURN: {
      click: 'return to tab',
      page: 'homepage',
      chapter1: 'navigation',
    },
  },

  // Favorite Apps events
  FAV_APPS: {
    SAVE: {
      click: 'save favorite apps',
      page: 'favorite apps',
      chapter1: 'apps',
    },
  },

  // Settings events (#858734) — the language selector. Only the successful save is
  // tagged: an aborted or failed change is not a language change. The chosen code is
  // merged into `chapter2` at dispatch so analytics break down per language, mirroring
  // the Alert Bar catalog above.
  SETTINGS: {
    SAVE_LANGUAGE: {
      click: 'save language',
      page: 'settings',
      chapter1: 'settings',
    },
  },

  // GES events
  GES: {
    VIEW: {
      page: 'ges',
      chapter1: 'GES',
      chapter2: 'Overview',
    },
  },

  // Dashboards events
  DASHBOARDS: {
    VIEW: {
      page: 'dashboards',
      chapter1: 'Dashboards',
      chapter2: 'Overview',
    },
    // Tab-view events (#881211) — chapter2 is the stable DashboardKey ('HR'/'HSE'),
    // not the translated tab label, so a future re-translation needs no analytics change.
    VIEW_HR_TAB: {
      page: 'dashboards',
      chapter1: 'Dashboards',
      chapter2: 'HR',
    },
    VIEW_HSE_TAB: {
      page: 'dashboards',
      chapter1: 'Dashboards',
      chapter2: 'HSE',
    },
    VIEW_LINKS_TAB: {
      page: 'dashboards',
      chapter1: 'Dashboards',
      chapter2: 'LINKS',
    },
  },

  // Integrated Management events (#801475)
  INTEGRATED_MANAGEMENT: {
    VIEW: {
      page: 'minhas tarefas',
      chapter1: 'MyApps',
      chapter2: 'Integrated Management',
    },
  },

  // Workforce Sizing events
  WORKFORCE_SIZING: {
    VIEW: {
      page: 'workforce sizing',
      chapter1: 'MyApps',
      chapter2: 'Workforce Sizing',
    },
    CLICK_CALCULATE: {
      click: 'calcular dimensionamento',
      page: 'workforce sizing',
      chapter1: 'workforce sizing',
    },
    CLICK_UPDATE_CALCULATION: {
      click: 'atualizar dimensionamento',
      page: 'workforce sizing',
      chapter1: 'workforce sizing',
    },
    FILLED_MEALS: {
      click: 'filled refeicoes',
      page: 'workforce sizing',
      chapter1: 'workforce sizing',
    },
    FILLED_SNACKS: {
      click: 'filled lanche desjejum',
      page: 'workforce sizing',
      chapter1: 'workforce sizing',
    },
    FILLED_EXTRAS: {
      click: 'filled extras',
      page: 'workforce sizing',
      chapter1: 'workforce sizing',
    },
    FILLED_HOURS: {
      click: 'filled horas colaborador',
      page: 'workforce sizing',
      chapter1: 'workforce sizing',
    },
    ERROR: {
      click: 'error on calcular',
      page: 'workforce sizing',
      chapter1: 'workforce sizing',
    },
    VIEW_RESULTS: {
      click: 'view calculation results',
      page: 'workforce sizing',
      chapter1: 'workforce sizing',
    },
    SELECT_SEGMENT: {
      click: 'selected segment',
      page: 'workforce sizing',
    },
  },

  // Alert Bar events (#812085) — the generic portal-shell alert bar (#805024 shell,
  // #800152 data). The three descriptors map 1:1 to the shell's controls; the alert's
  // per-alert identity (the stable BFF `id`) is merged into `chapter2` at dispatch so
  // analytics break down per alert. `page` defaults to 'homepage' (where the bar renders
  // today) and is overridable per dispatch.
  // NOTE: `click` / `page` / `chapter1` are the refinement-note proposed defaults, pending
  // tagging-plan confirmation by PO/analytics — see #812085.
  ALERT_BAR: {
    DISMISS: {
      click: 'close alert bar',
      page: 'homepage',
      chapter1: 'alert bar',
    },
    CTA: {
      click: 'alert bar cta',
      page: 'homepage',
      chapter1: 'alert bar',
    },
    ACKNOWLEDGE: {
      click: 'acknowledge alert bar',
      page: 'homepage',
      chapter1: 'alert bar',
    },
  },

  // Notification Bell events (#805025) — the portal-shell notification bell + inbox
  // (desktop dropdown / mobile full-screen view, sharing this one data layer & catalog).
  // `page` defaults to 'homepage' (the bell renders portal-wide) and is overridable per
  // dispatch; each item event carries the notification's `broadcastId` in `chapter2` so
  // analytics break down per notification, mirroring the Alert Bar catalog above.
  // NOTE: proposed defaults, pending tagging-plan confirmation by PO/analytics.
  NOTIFICATION_BELL: {
    OPEN: {
      click: 'open notification bell',
      page: 'homepage',
      chapter1: 'notification bell',
    },
    ITEM_CLICK: {
      click: 'open notification item',
      page: 'homepage',
      chapter1: 'notification bell',
    },
  },

  // OSAT events (#806379) — the OSAT satisfaction-survey modal (feature #800524, spec
  // #802202). This catalog owns the consts + params only; the call sites are added in the
  // owning PRs referencing PIANO_EVENTS.OSAT.*: VIEW → #804759 (trigger, on open);
  // SELECT_RATING → #801487 (StarRating onChange); SHARE_FEEDBACK / RESPOND_LATER /
  // CLOSE → #801487 / #804759 (submit / dismiss). Casing mirrors the MyApps conventions
  // (Integrated Management catalog #801475): the pageDisplay VIEW carries chapter1:'MyApps'
  // + chapter2:'OSAT'; the clickActions use a lowercase verb+noun click, lowercase
  // chapter1:'osat', and no chapter3.
  // NOTE: event names/params are the spec proposal, pending confirmation against the
  // All-Apps Tagging Plan "NPS lines" before merge (spec #802202 OQ-1). The optional
  // SUBMIT_SUCCESS / SUBMIT_ERROR events (OQ-3) are out of scope for this task.
  // Pop-up banner events (#885563) — the Self-Promotion login-time modal (story #804789).
  // Names come from the story's own acceptance criteria: view_popup_banner /
  // click_on_popup_cta / close_popup_banner. Casing mirrors the OSAT catalog above: the
  // pageDisplay VIEW carries chapter1:'MyApps' + chapter2, the clickActions use a lowercase
  // verb+noun click and a lowercase chapter1.
  BANNER: {
    VIEW: {
      page: 'popup_banner',
      chapter1: 'MyApps',
      chapter2: 'Self Promotion',
    },
    CLICK_CTA: {
      click: 'click on popup cta',
      page: 'popup_banner',
      chapter1: 'popup_banner',
    },
    CLOSE: {
      click: 'close popup banner',
      page: 'popup_banner',
      chapter1: 'popup_banner',
    },
  },

  OSAT: {
    VIEW: {
      page: 'osat',
      chapter1: 'MyApps',
      chapter2: 'OSAT',
    },
    SELECT_RATING: {
      click: 'select rating',
      page: 'osat',
      chapter1: 'osat',
    },
    SHARE_FEEDBACK: {
      click: 'share feedback',
      page: 'osat',
      chapter1: 'osat',
    },
    RESPOND_LATER: {
      click: 'respond later',
      page: 'osat',
      chapter1: 'osat',
    },
    CLOSE: {
      click: 'close',
      page: 'osat',
      chapter1: 'osat',
    },
  },

  // Accessibility declaration events (#871923) — the static French RGAA declaration at
  // /accessibility. `LINK` carries the activated link's key in `chapter3` (merged at dispatch)
  // so the four outbound targets stay filterable under one click name. `SCROLL` is a
  // clickAction by decision, not by preference: @sdx/b2o-fe-tools/pianoAnalytics exposes only
  // pageDisplay and clickAction — there is no scroll primitive — so the depth milestone
  // ('25' | '50' | '75' | '100') rides in `chapter3` the same way.
  ACCESSIBILITY: {
    VIEW: {
      page: 'accessibility',
      chapter1: 'MyApps',
      chapter2: 'Accessibility',
    },
    LINK: {
      click: 'open accessibility link',
      page: 'accessibility',
      chapter1: 'accessibility',
    },
    SCROLL: {
      click: 'scroll accessibility page',
      page: 'accessibility',
      chapter1: 'accessibility',
    },
  },

  // Support Center events (#1072503)
  SUPPORT_CENTER: {
    VIEW: {
      page: 'support center',
      chapter1: 'MyApps',
      chapter2: 'Support Center',
    },
    ERROR_LOAD_PAGE: {
      click: 'error load support center',
      page: 'support center',
      chapter1: 'support center',
    },
    ERROR_LOAD_SECTION: {
      click: 'error load section',
      page: 'support center',
      chapter1: 'support center',
    },
  },
} as const;
